import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { logout as logoutAction } from './authSlice';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Composite sign-out: best-effort `POST /api/auth/revoke` (with the active
 * `sessionId`) to invalidate the server-side session, then clears the local
 * Redux store regardless of the server response. We never block UI on the
 * network call — if it fails the client is still logged out locally and
 * the next request will 401.
 *
 * Uses a dynamic import so `@kss/auth` does not declare a static dependency
 * on `@kss/api` (which would risk a circular package graph).
 */
export function useSignOut() {
  const dispatch = useDispatch();
  const sessionId = useSelector((s: RootWithAuth) => s.auth.session?.sessionId);
  return useCallback(async () => {
    try {
      const api = await import('@kss/api');
      if (sessionId) {
        await api.authService.revoke({ sessionId });
      }
    } catch {
      /* network/api error — proceed with local logout anyway */
    } finally {
      dispatch(logoutAction());
    }
  }, [dispatch, sessionId]);
}
